import React, { createContext, useContext, useState, useEffect } from 'react'
import { apiService, ApiError } from '../services/api'

interface Admin {
  id: number
  email: string
  full_name: string
  is_active: boolean
  is_superuser: boolean
  role: string
  permissions_list: string[]
}

type LoginResult = 
  | { success: true }
  | { success: false; requires2fa: true; tempToken: string }

interface AuthContextType {
  isAuthenticated: boolean
  admin: Admin | null
  login: (email: string, password: string) => Promise<LoginResult>
  loginWith2fa: (tempToken: string, token: string) => Promise<boolean>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [admin, setAdmin] = useState<Admin | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if admin is already logged in (from localStorage)
    const token = localStorage.getItem('admin_token')
    
    if (token) {
      // Verify token with backend
      apiService.getCurrentAdmin()
        .then((adminData) => {
          if (adminData.is_active) {
            setIsAuthenticated(true)
            setAdmin(adminData)
          } else {
            // Admin is not active, clear token
            localStorage.removeItem('admin_token')
            localStorage.removeItem('admin_refresh_token')
            localStorage.removeItem('admin_user')
          }
        })
        .catch(() => {
          // Token is invalid, clear it
          localStorage.removeItem('admin_token')
          localStorage.removeItem('admin_refresh_token')
          localStorage.removeItem('admin_user')
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [])

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      setLoading(true)
      const response = await apiService.login(email, password)
      
      if (response.requires_2fa && response.temp_token) {
        return { success: false, requires2fa: true, tempToken: response.temp_token }
      }
      
      const adminData = response.admin
      if (!adminData?.is_active) {
        throw new Error('Access denied. Admin account is inactive.')
      }
      
      localStorage.setItem('admin_token', response.access_token!)
      localStorage.setItem('admin_refresh_token', response.refresh_token!)
      localStorage.setItem('admin_user', JSON.stringify(adminData))
      
      setIsAuthenticated(true)
      setAdmin(adminData)
      return { success: true }
    } catch (error) {
      console.error('Login error:', error)
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Invalid email or password. Please check your credentials and try again.')
        } else if (error.status === 403) {
          throw new Error('Access denied. Your account may be inactive or you lack sufficient permissions.')
        } else {
          throw new Error(error.message || 'Authentication failed. Please try again.')
        }
      }
      throw new Error('Login failed. Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  const loginWith2fa = async (tempToken: string, token: string): Promise<boolean> => {
    try {
      setLoading(true)
      const response = await apiService.verify2faAdmin(tempToken, token)
      const adminData = response.admin
      if (!adminData?.is_active) {
        throw new Error('Access denied. Admin account is inactive.')
      }
      localStorage.setItem('admin_token', response.access_token)
      localStorage.setItem('admin_refresh_token', response.refresh_token)
      localStorage.setItem('admin_user', JSON.stringify(adminData))
      setIsAuthenticated(true)
      setAdmin(adminData)
      return true
    } catch (error) {
      console.error('2FA verification error:', error)
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Invalid 2FA code. Please try again.')
        }
        throw new Error(error.message || 'Verification failed.')
      }
      throw new Error('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_refresh_token')
    localStorage.removeItem('admin_user')
    setIsAuthenticated(false)
    setAdmin(null)
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, admin, login, loginWith2fa, logout, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
